import {
  Box,
  Button,
  Container,
  IconButton,
  Link as MUILink,
  Paper,
  Slide,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Close from "@mui/icons-material/Close";

import useResponsive from "@/hooks/useResponsive";
import { PRIVACY_POLICY_URL } from "@/utils/cookieConsent";
import CookieLogo from "@/assets/icons/cookie_logo.svg?react";

interface CookieBannerProps {
  open: boolean;
  onAcceptAll: () => void;
  onRejectNonEssential: () => void;
  onCustomize: () => void;
  onClose: () => void;
}

const CookieBanner = ({
  open,
  onAcceptAll,
  onRejectNonEssential,
  onCustomize,
  onClose,
}: CookieBannerProps) => {
  const theme = useTheme();
  const isMobile = useResponsive("down", "md");

  return (
    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
      <Box
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: theme.zIndex.modal,
          px: { xs: 1.5, md: 3 },
          pb: { xs: 1.5, md: 3 },
          pointerEvents: "none",
        }}
      >
        <Container disableGutters sx={{ pointerEvents: "auto" }}>
          <Paper
            elevation={6}
            sx={{
              position: "relative",
              borderRadius: { xs: 2, md: 3 },
              border: `1px solid ${theme.palette.grey[200]}`,
              p: { xs: 2.5, md: 3 },
              pr: { xs: 5, md: 3 },
              backgroundColor: theme.palette.common.white,
            }}
          >
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Reject non-essential cookies"
              sx={{
                display: { xs: "inline-flex", md: "none" },
                position: "absolute",
                top: 8,
                right: 8,
                color: theme.palette.grey[600],
              }}
            >
              <Close fontSize="small" />
            </IconButton>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 2, md: 3 }}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack
                direction="row"
                spacing={{ xs: 1.5, md: 2 }}
                alignItems={{ xs: "center", md: "flex-start" }}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: { xs: 40, md: 70 },
                    height: { xs: 40, md: 70 },
                    "& svg": {
                      width: "100%",
                      height: "100%",
                      paddingTop: { xs: 0, md: "25%" },
                    },
                  }}
                  aria-hidden="true"
                >
                  <CookieLogo />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="h6"
                    component="p"
                    color="text.primary"
                    sx={{ mb: { xs: 0, md: 0.5 } }}
                  >
                    We use cookies
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: { xs: "none", md: "block" },
                      lineHeight: 1.5,
                      maxWidth: "70%",
                    }}
                  >
                    We use cookies to improve your experience, analyze traffic, and
                    personalize content. You can accept all cookies, reject non-essential
                    cookies, or customize your preferences.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: { xs: "none", md: "block" },
                      mt: 1,
                    }}
                  >
                    Learn more in our{" "}
                    <MUILink
                      href={PRIVACY_POLICY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="always"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        textDecoration: "underline",
                      }}
                    >
                      Privacy Policy
                    </MUILink>
                    .
                  </Typography>
                </Box>
              </Stack>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: { xs: "block", md: "none" },
                  lineHeight: 1.5,
                  maxWidth: "70%",
                  "@media (max-width: 700px)": {
                    maxWidth: "100%",
                    padding: "0 15%",
                    margin: "0",  
                  },
                }}
              >
                We use cookies to improve your experience, analyze traffic, and
                personalize content.
              </Typography>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 1, md: 1.5 }}
                sx={{
                  flexShrink: 0,
                  width: { xs: "100%", md: "auto" },
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size={isMobile ? "medium" : "large"}
                  onClick={onAcceptAll}
                  fullWidth={isMobile}
                  sx={{ width: { xs: "100%", md: 222 } }}
                >
                  Accept All
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size={isMobile ? "medium" : "large"}
                  onClick={onRejectNonEssential}
                  fullWidth={isMobile}
                >
                  Reject Non-Essential
                </Button>
                <Button
                  variant="text"
                  color="primary"
                  size={isMobile ? "medium" : "large"}
                  onClick={onCustomize}
                  fullWidth={isMobile}
                  sx={{ fontWeight: 700, textDecoration: "underline" }}
                >
                  Customize Settings
                </Button>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: { xs: "block", md: "none" },
                  textAlign: "center",
                }}
              >
                Learn more in our{" "}
                <MUILink
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    textDecoration: "underline",
                  }}
                >
                  Privacy Policy
                </MUILink>
                .
              </Typography>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </Slide>
  );
};

export default CookieBanner;
